import React, { useState } from 'react';
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAdminUsers } from '../../hooks/admin/useAdminUsers';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';

const TenantManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Mock users data - would be replaced with real API call
  const mockUsers = [
    {
      id: 'usr_001',
      name: 'Sarah Johnson',
      email: 'sarah@company.com',
      company: 'Johnson Media Group',
      plan: 'Enterprise',
      status: 'active',
      created: '2024-01-15T10:30:00Z',
      lastLogin: '2024-03-20T10:30:00Z',
      connections: 8,
      apiUsage: 145000,
      totalSpent: 2450.00
    },
    {
      id: 'usr_002',
      name: 'Mike Chen',
      email: 'mike@startup.io',
      company: 'StartupIO',
      plan: 'Professional',
      status: 'active',
      created: '2024-02-20T09:15:00Z',
      lastLogin: '2024-03-20T09:15:00Z',
      connections: 5,
      apiUsage: 89000,
      totalSpent: 890.00
    },
    {
      id: 'usr_003',
      name: 'Emma Wilson',
      email: 'emma@agency.com',
      company: 'Creative Agency Ltd',
      plan: 'Professional',
      status: 'suspended',
      created: '2023-11-10T14:22:00Z',
      lastLogin: '2024-03-10T14:22:00Z',
      connections: 3,
      apiUsage: 234000,
      totalSpent: 1200.00
    },
    {
      id: 'usr_004',
      name: 'David Rodriguez',
      email: 'david@freelance.com',
      company: 'Freelance',
      plan: 'Basic',
      status: 'active',
      created: '2024-03-01T08:00:00Z',
      lastLogin: '2024-03-19T16:45:00Z',
      connections: 2,
      apiUsage: 12000,
      totalSpent: 0.00
    }
  ];

  const users = mockUsers; // Would use: const { users, loading, error, suspendUser, activateUser, deleteUser } = useAdminUsers();

  const handleUserAction = async (action, userId) => {
    setActionLoading(`${action}-${userId}`);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    console.log(`${action} user ${userId}`);
    setActionLoading(null);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.company.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesPlan = planFilter === 'all' || user.plan.toLowerCase() === planFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesPlan;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage user accounts, subscriptions, and access</p>
        </div>
        <button className="bg-admin-600 text-white px-4 py-2 rounded-lg hover:bg-admin-700 transition-colors flex items-center space-x-2">
          <UserPlusIcon className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-admin-500 focus:border-admin-500"
            />
          </div>

          <div className="flex space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-admin-500 focus:border-admin-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="pending">Pending</option>
            </select>

            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-admin-500 focus:border-admin-500"
            >
              <option value="all">All Plans</option>
              <option value="enterprise">Enterprise</option>
              <option value="professional">Professional</option>
              <option value="basic">Basic</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connections
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  API Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.company}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={user.plan} type="plan" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={user.status} type="user" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.connections}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.apiUsage.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(user.totalSpent)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.lastLogin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserModal(true);
                        }}
                        className="text-admin-600 hover:text-admin-900"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>

                      <button
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit User"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      {user.status === 'active' ? (
                        <button
                          onClick={() => handleUserAction('suspend', user.id)}
                          disabled={actionLoading === `suspend-${user.id}`}
                          className="text-warning-600 hover:text-warning-900"
                          title="Suspend User"
                        >
                          {actionLoading === `suspend-${user.id}` ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <NoSymbolIcon className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUserAction('activate', user.id)}
                          disabled={actionLoading === `activate-${user.id}`}
                          className="text-success-600 hover:text-success-900"
                          title="Activate User"
                        >
                          {actionLoading === `activate-${user.id}` ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <CheckCircleIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}

                      <button
                        onClick={() => handleUserAction('delete', user.id)}
                        disabled={actionLoading === `delete-${user.id}`}
                        className="text-danger-600 hover:text-danger-900"
                        title="Delete User"
                      >
                        {actionLoading === `delete-${user.id}` ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full m-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">User Details</h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-4">Account Information</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-medium">{selectedUser.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Company</p>
                    <p className="font-medium">{selectedUser.company}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Plan</p>
                    <StatusBadge status={selectedUser.plan} type="plan" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Status</p>
                    <StatusBadge status={selectedUser.status} type="user" />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-4">Usage & Activity</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">API Calls (30d)</p>
                    <p className="font-medium">{selectedUser.apiUsage.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Connected Platforms</p>
                    <p className="font-medium">{selectedUser.connections}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Spent</p>
                    <p className="font-medium">{formatCurrency(selectedUser.totalSpent)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Account Created</p>
                    <p className="font-medium">{formatDate(selectedUser.created)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Last Login</p>
                    <p className="font-medium">{formatDate(selectedUser.lastLogin)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t flex justify-end space-x-3">
              <button className="px-4 py-2 text-gray-700 hover:text-gray-900">
                View Activity Log
              </button>
              <button className="px-4 py-2 text-gray-700 hover:text-gray-900">
                Reset Password
              </button>
              <button className="px-4 py-2 bg-admin-600 text-white rounded-lg hover:bg-admin-700">
                Edit User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
